/**
 * React Query hook for Invoice List V2 with RDS-based pagination
 * Uses the new listInvoiceV2 API with improved filtering and sorting
 */

import { useQuery } from '@tanstack/react-query';
import { listInvoiceV2 } from 'services/billingServices';
import { queryKeys } from '../../../config/queryClient';

/**
 * Fetches invoices data with RDS-based pagination
 * API Response format: { total_count, data: [], page_count, page, limit }
 *
 * @param {Object} params - Request parameters
 * @returns {Promise<Object>} - Response with data array and pagination metadata
 */
const fetchInvoicesV2 = async ({
  limit = 10,
  page = 1,
  invoice_status = '',
  practice_area_list = [],
  case_id = '',
  invoice_date_lower = '',
  invoice_date_upper = '',
  sort_by,
  sort_order,
  search_key = '',
  lead_attorney_list = [],
  timezone_offset = '',
}) => {
  try {
    const response = await listInvoiceV2({
      limit,
      page,
      invoice_status,
      practice_area_list,
      case_id,
      invoice_date_lower,
      invoice_date_upper,
      sort_by,
      sort_order,
      search_key,
      lead_attorney_list,
      timezone_offset,
    });

    if (response?.data) {
      return response.data;
    }

    // Return empty structure if no data (matching API format)
    return {
      total_count: 0,
      data: [],
      page_count: 0,
      page: page,
      limit: limit,
    };
  } catch (error) {
    console.error('Error fetching invoices V2:', error);
    throw error;
  }
};

/**
 * Hook to fetch invoices with RDS-based pagination using React Query
 *
 * @param {Object} options - Configuration options
 * @param {number} options.limit - Items per page (default: 10)
 * @param {number} options.page - Page number (default: 1)
 * @param {string} options.invoice_status - Filter by status (e.g., 'UNPAID', 'PAID', 'LATE', 'PARTIAL')
 * @param {Array} options.practice_area_list - Filter by practice area IDs
 * @param {string} options.case_id - Filter by case ID
 * @param {string} options.invoice_date_lower - Start date filter (ISO 8601 format)
 * @param {string} options.invoice_date_upper - End date filter (ISO 8601 format)
 * @param {Array} options.lead_attorney_list - Filter by lead attorney UUIDs
 * @param {string} options.sort_by - Field to sort by
 * @param {string} options.sort_order - Sort order ('asc' or 'desc')
 * @param {string} options.search_key - Search keyword for filtering invoices
 * @param {string} options.timezone_offset - User timezone offset for API (e.g. from getTimezoneOffset)
 * @param {boolean} options.enabled - Enable/disable query (default: true)
 *
 * @returns {Object} Query result object
 * @returns {Array} returns.invoices - Array of invoice objects
 * @returns {number} returns.totalRecords - Total number of records
 * @returns {number} returns.currentPage - Current page number
 * @returns {number} returns.totalPages - Total number of pages
 * @returns {number} returns.pageSize - Items per page
 * @returns {boolean} returns.isLoading - Initial loading state
 * @returns {boolean} returns.isFetching - Fetching state (including background refetch)
 * @returns {boolean} returns.isError - Error state indicator
 * @returns {Object|null} returns.error - Error object if request failed
 * @returns {Function} returns.refetch - Function to manually refetch data
 *
 * @example
 * const {
 *   invoices,
 *   totalRecords,
 *   totalPages,
 *   isLoading,
 *   refetch
 * } = useInvoiceListV2Query({
 *   limit: 10,
 *   page: 1,
 *   invoice_status: 'UNPAID',
 *   practice_area_list: ['PA#001'],
 *   sort_by: 'sent_date',
 *   sort_order: 'desc',
 * });
 */
export const useInvoiceListV2Query = ({
  limit = 10,
  page = 1,
  invoice_status = '',
  practice_area_list = [],
  case_id = '',
  invoice_date_lower = '',
  invoice_date_upper = '',
  sort_by,
  sort_order,
  search_key = '',
  lead_attorney_list = [],
  timezone_offset = '',
  enabled = true,
} = {}) => {
  // Create stable query key based on all filter parameters
  const queryKey = queryKeys.invoices.listV2(
    {
      invoice_status,
      practice_area_list,
      case_id,
      invoice_date_lower,
      invoice_date_upper,
      sort_by,
      sort_order,
      search_key,
      lead_attorney_list,
      timezone_offset,
    },
    page,
    limit
  );

  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey,
    queryFn: () =>
      fetchInvoicesV2({
        limit,
        page,
        invoice_status,
        practice_area_list,
        case_id,
        invoice_date_lower,
        invoice_date_upper,
        sort_by,
        sort_order,
        search_key,
        lead_attorney_list,
        timezone_offset,
      }),
    enabled,
    staleTime: 5 * 60 * 1000, // Consider data stale after 5 minutes
    cacheTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
    keepPreviousData: true, // Keep previous data while fetching
    refetchOnWindowFocus: false, // Don't refetch on window focus
    refetchOnMount: enabled, // Only refetch on mount if query is enabled
    retry: (failureCount) => {
      // Retry twice for errors
      return failureCount < 2;
    },
  });

  return {
    invoices: data?.data || [],
    totalRecords: data?.total_count || 0,
    currentPage: data?.page || page,
    totalPages: data?.page_count || 0,
    pageSize: data?.limit || limit,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  };
};

export default useInvoiceListV2Query;
